import SyncIcon from '@mui/icons-material/Sync';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import Flex from '@Components/Bases/Flex';
import ConfirmModal from '@Components/Modal/ConfirmModal';
import OtherExplanation from '@Containers/OtherExplanations/OtherExplanations';
import {
  useGetEsgGreenhouse,
  useGetEsgGreenhouseOneReport,
} from '@Domains/Esg/EsgGreenhouse';
import { useDisclosure } from '@hooks/useDisclosure';
import { convertBuddhistYearToGregorian } from '@Utils/datetime';
import { useProgressBar } from 'src/Constant/ProgressBarsContext/ProgressBar.Context';
import { useProfile } from 'src/Constant/UserProfileContext/UserProfileProvider';
import ClimateChangeStructure from './Containers/ClimateChangeStructure/ClimateChangeStructure';
import EsgGreenhouseCheckAll from './Containers/EsgGreenhouseCheckAll';
import EsgGreenhouseFooter from './Containers/EsgGreenhouseFooter';
import GreenhouseFormProvider from './Containers/EsgGreenhouseFormProvider/EsgGreenhouseFormProvider';
import { IGreenhouseFormValues } from './Containers/EsgGreenhouseFormProvider/types';
import GreenhouseGasMgmtReport from './Containers/GreenhouseGasMgmtReport';
import GreenhouseGoalTerm from './Containers/GreenhouseGoalTerm';
import GreenhouseStandard from './Containers/GreenhouseStandard';
import GreenhouseStrategy from './Containers/GreenhouseStrategy';
import { mapEsgGreenhouseToFormValues } from './utils';

const EsgGreenhouse = () => {
  const { id = '' } = useParams();
  const location = useLocation();
  const { user } = useProfile();
  const { isPublished, progressData, refetchProgressWithUpdate } =
    useProgressBar();
  const currentPath = location.pathname.split('/').pop();

  const [oneReportData, setOneReportData] = useState<
    IGreenhouseFormValues | undefined
  >(undefined);

  const iCompany = user?.I_COMPANY;
  const startOperatingYear = convertBuddhistYearToGregorian(
    progressData?.report.startOperatingYear
  );
  const previousStartOperatingYear = startOperatingYear - 1;

  const [{ data = {}, loading, error }, refetch] = useGetEsgGreenhouse(id);
  const [
    { loading: loadingOneReport, error: errorOneReport },
    executeOneReport,
  ] = useGetEsgGreenhouseOneReport(
    id,
    Number(iCompany),
    previousStartOperatingYear
  );

  const [
    openedConfirmModal,
    { open: openConfirmModal, close: closeConfirmModal },
  ] = useDisclosure();
  const [openedSnackbar, { open: openSnackbar, close: closeSnackbar }] =
    useDisclosure();

  useEffect(() => {
    if (currentPath === 'greenhouse') {
      refetch().catch((err) => {
        if (err.name !== 'CanceledError') {
          console.error('Failed to fetch esg greenhouse :', err);
        }
      });
    }
  }, [currentPath, refetch]);

  const fetchOneReportData = useCallback(async () => {
    try {
      const response = await executeOneReport();

      if (response.data) {
        setOneReportData(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch esg greenhouse one report:', error);
      setOneReportData(undefined);
    } finally {
      openSnackbar();
      closeConfirmModal();
      refetchProgressWithUpdate(true);
      refetch();
    }
  }, [
    executeOneReport,
    openSnackbar,
    closeConfirmModal,
    refetchProgressWithUpdate,
    refetch,
  ]);

  if (loading) {
    return (
      <Flex justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress size={50} />
      </Flex>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity={'error'}>
          ไม่สามารถโหลดข้อมูลการเปิดเผยข้อมูลเกี่ยวข้องกับการปล่อยก๊าซเรือนกระจกได้
        </Alert>
      </Box>
    );
  }

  const initialGhFormValues = mapEsgGreenhouseToFormValues(data, oneReportData);

  return (
    <>
      <GreenhouseFormProvider initialValues={initialGhFormValues}>
        <Flex flexDirection={'column'} gap={2} p={2} mb={8}>
          <Flex justifyContent={'space-between'} alignItems={'center'}>
            <Typography variant="h3">ส่วนที่ 3 แผนการยกระดับ</Typography>
            {!isPublished && (
              <Button
                color={'inherit'}
                startIcon={<SyncIcon />}
                onClick={openConfirmModal}
              >
                ดึงข้อมูลจาก ESG Data Platform
              </Button>
            )}
          </Flex>
          <GreenhouseStandard />
          <GreenhouseGoalTerm />
          <GreenhouseStrategy />
          <ClimateChangeStructure />
          <GreenhouseGasMgmtReport />
          <OtherExplanation />
          <EsgGreenhouseCheckAll />
        </Flex>
        <EsgGreenhouseFooter />
      </GreenhouseFormProvider>

      <ConfirmModal
        opened={openedConfirmModal}
        close={closeConfirmModal}
        loading={loadingOneReport}
        disableBackdropClose
        topic="ดึงข้อมูลล่าสุด"
        description={`เมื่อกดยืนยันระบบจะดึงข้อมูลทุกรายการจากระบบ ESG Data Platform \n
    และจะทำการแทนที่ข้อมูลที่ได้กรอกไว้ก่อนหน้านี้`}
        confirm={fetchOneReportData}
      />

      <Snackbar
        open={openedSnackbar}
        autoHideDuration={2000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={!!errorOneReport ? 'error' : 'success'}
          onClose={closeSnackbar}
          sx={{
            alignItems: 'center',
            marginBottom: 6,
            '.MuiAlert-icon': { py: 1 },
            '.MuiAlert-message': { py: 1 },
          }}
        >
          {errorOneReport?.status === 404
            ? 'ไม่พบข้อมูลจาก ESG Data Platform'
            : !!errorOneReport
            ? 'เกิดข้อผิดพลาดในการดึงข้อมูล'
            : 'ดึงข้อมูลสำเร็จ'}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EsgGreenhouse;
